import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * TenantSelectionModal
 *
 * Displayed when a user/owner is associated with multiple tenants.
 * The user picks which tenant to log into for this session.
 *
 * Props:
 *   tenants         – Array of { id, companyname, logo }
 *   onSelect        – (tenantId) => void  – called when a tenant is chosen
 *   onCancel        – () => void           – called when modal is dismissed
 *   isLoading       – boolean              – disables buttons while request in-flight
 *   onAddNewTenant  – () => void           – (optional) called when "Add New Tenant" is clicked
 */
function TenantSelectionModal({ tenants, onSelect, onCancel, isLoading, onAddNewTenant }) {
  const { t } = useTranslation();
  const [selectedId, setSelectedId] = useState(null);

  const handleConfirm = () => {
    if (selectedId != null) onSelect(selectedId);
  };

  return (
    // `keep-default-theme` opts this modal out of the Minimal theme overrides
    // in styles/overrides.css so it always renders with the standard
    // light-modal look regardless of the user's theme choice.
    <div className="modal-overlay keep-default-theme" onClick={onCancel}>
      <div
        className="modal-content modal-light"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 420, width: '90%', padding: 24, background: '#ffffff' }}
      >
        <h4 className="hdr hdr--primary" style={{ marginBottom: 16 }}>
          {t('auth.selectTenantTitle', 'Select an Account')}
        </h4>
        <p style={{ marginBottom: 16, fontSize: 14, opacity: 0.8 }}>
          {t(
            'auth.selectTenantDescription',
            'Your credentials are associated with multiple accounts. Please choose which one to use for this session.'
          )}
        </p>

        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {tenants.map((tenant) => (
            <li key={tenant.id} style={{ marginBottom: 8 }}>
              <button
                type="button"
                onClick={() => setSelectedId(tenant.id)}
                disabled={isLoading}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '12px 16px',
                  border: selectedId === tenant.id ? '2px solid #0066cc' : '2px solid transparent',
                  borderRadius: 8,
                  background: selectedId === tenant.id ? 'rgba(0,102,204,0.08)' : 'rgba(0,0,0,0.03)',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  textAlign: 'left',
                  fontSize: 15,
                  transition: 'border-color 0.15s, background 0.15s'
                }}
              >
                {tenant.logo ? (
                  <img
                    src={tenant.logo}
                    alt=""
                    style={{ width: 32, height: 32, borderRadius: 4, objectFit: 'cover' }}
                  />
                ) : (
                  <span
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 4,
                      background: '#0066cc',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                      fontSize: 14,
                      flexShrink: 0
                    }}
                  >
                    {tenant.companyname?.charAt(0)?.toUpperCase() || '?'}
                  </span>
                )}
                <span style={{ fontWeight: 500 }}>{tenant.companyname}</span>
              </button>
            </li>
          ))}
        </ul>

        {onAddNewTenant && (
          <button
            type="button"
            onClick={onAddNewTenant}
            disabled={isLoading}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '12px 16px',
              marginTop: 8,
              border: '2px dashed #0066cc',
              borderRadius: 8,
              background: 'transparent',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              textAlign: 'left',
              fontSize: 15,
              color: '#0066cc'
            }}
          >
            <span
              style={{
                width: 32,
                height: 32,
                borderRadius: 4,
                background: '#0066cc',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 700,
                fontSize: 18,
                flexShrink: 0
              }}
            >
              +
            </span>
            <span style={{ fontWeight: 500 }}>
              {t('auth.addNewTenant', 'Add New Tenant')}
            </span>
          </button>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 20, justifyContent: 'flex-end' }}>
          <button
            type="button"
            className="btn"
            onClick={onCancel}
            disabled={isLoading}
            style={{ minWidth: 80 }}
          >
            {t('common.cancel', 'Cancel')}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleConfirm}
            disabled={isLoading || selectedId == null}
            style={{ minWidth: 80 }}
          >
            {isLoading
              ? t('auth.loggingInButton', 'Logging in...')
              : t('auth.continueButton', 'Continue')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default TenantSelectionModal;
